import React, { useState } from "react"
import {
  Modal,
  Upload,
  Button,
  Typography,
  Divider,
  Alert,
  Collapse,
  Tag,
  List,
  Space,
} from "antd"
import { notify } from "shared/ui/Notifications/notify"
import {
  InboxOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  ReloadOutlined,
} from "@ant-design/icons"
import type { UploadProps, UploadFile } from "antd"
import { Editor as TiptapEditor } from "@tiptap/react"
import { DOMParser as ProseMirrorDOMParser } from "@tiptap/pm/model"
import { escapeHTML } from "utils/escapeHtml"

import { useAIConversion } from "features/QuestionnaireEditor/api/useAIConversion"
import { useExportMapFill } from "features/QueryConsole/api/useExportMapFill"
import {
  failedPartsPhrase,
  shouldReplaceStored,
} from "features/QueryConsole/components/ExportMap/utils/inventoryPolicy"
import { useAsync } from "hooks/core/useAsync"
import { AIConversionResponse } from "types/AIConversion.types"
import { SourceCodeInventory } from "types/sourceCodes.types"
import { DotLottieReact } from "@lottiefiles/dotlottie-react"

const { Dragger } = Upload
const { Text, Paragraph } = Typography
const { Panel } = Collapse

interface AIConversionModalProps {
  /** Controls modal visibility */
  isVisible: boolean
  /** Function to close the modal */
  onClose: () => void
  /** Project ID for the conversion */
  projectId: string
  /** Editor instance to insert converted content */
  editor: TiptapEditor | null
  /**
   * Called when the converted content is inserted into the editor, with what
   * the conversion captured alongside it (0 when the document carried no codes).
   */
  onInserted?: (info: { sourceCodesCaptured: number }) => void
}

/**
 * Modal component for AI-powered questionnaire conversion.
 * Allows users to upload DOCX files and convert them to DATA(Q) format using AI.
 */
const AIConversionModal: React.FC<AIConversionModalProps> = ({
  isVisible,
  onClose,
  projectId,
  editor,
  onInserted,
}) => {
  // State management
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [conversionResult, setConversionResult] =
    useState<AIConversionResponse | null>(null)
  const [showResults, setShowResults] = useState<boolean>(false)
  const [isConverting, setIsConverting] = useState<boolean>(false)

  // Hook for AI conversion operations
  const { convertQuestionnaire, isConvertingQuestionnaire } = useAIConversion()
  const { saveInventory, loadInventory } = useExportMapFill()
  const { pollingAsync, getAsyncResult } = useAsync()

  /**
   * Stores the code inventory the converter read from the document with the
   * project, so "Fill export map from source document" can use it without a
   * second upload. Stored on arrival (not on insert): the codes describe the
   * document, not what the user chooses to do with the conversion.
   *
   * Storing is conditional. A conversion whose document chunks did not all come
   * back holds only part of the questionnaire, and writing that over a complete
   * inventory is how a later fill ended up matching against half a document.
   * The project's own copy decides (see shouldReplaceStored); when it wins, the
   * user is told rather than left thinking these codes were kept.
   *
   * @param result - The conversion response just received
   */
  const storeSourceCodes = async (
    result: AIConversionResponse
  ): Promise<void> => {
    const inventory = result?.data?.sourceCodes
    if (!inventory?.entries?.length || !projectId) return

    // A lookup failure is no reason to lose the codes: fall back to storing,
    // which is what this path did before the policy existed.
    let existing: SourceCodeInventory | null = null
    try {
      existing = (await loadInventory(projectId)) ?? null
    } catch (error: unknown) {
      console.warn(
        "Could not read the stored source codes; storing this extraction:",
        error
      )
    }

    const failedParts = failedPartsPhrase(inventory)

    if (!shouldReplaceStored(existing, inventory).replace) {
      notify.toast.info(
        failedParts
          ? `Source codes from this conversion were not stored: this extraction was incomplete (${failedParts} failed)`
          : "Source codes from this conversion were not stored: the project already has a more complete set"
      )
      return
    }

    try {
      await saveInventory(projectId, inventory)
      if (failedParts) {
        notify.toast.warning(
          `Source codes stored, but ${failedParts} of the document failed to extract; the codes are incomplete`
        )
      }
    } catch (error: unknown) {
      console.warn("Could not store source codes with the project:", error)
      notify.toast.warning(
        "Source codes could not be stored; re-upload the document when filling the export map"
      )
    }
  }

  /**
   * Formats processing time from milliseconds to minutes and seconds
   * Currently only used here, but could be moved to a utility file if needed elsewhere
   * @param {number} ms - Time in milliseconds
   * @returns {string} Formatted time string
   */
  const formatProcessingTime = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000)
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60

    if (minutes > 0) {
      return `${minutes}m ${seconds}s`
    }
    return `${seconds}s`
  }

  /**
   * Handles file upload configuration
   */
  const uploadProps: UploadProps = {
    name: "document",
    multiple: false,
    fileList,
    accept: ".docx",
    beforeUpload: (file) => {
      // Validate file type
      const isDocx =
        file.type ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      if (!isDocx) {
        notify.toast.error("Please upload a .docx file only!")
        return false
      }

      // Validate file size (10MB limit)
      const isLt10M = file.size / 1024 / 1024 < 10
      if (!isLt10M) {
        notify.toast.error("File must be smaller than 10MB!")
        return false
      }

      // Update file list
      setFileList([file])
      return false // Prevent automatic upload
    },
    onRemove: () => {
      setFileList([])
      setConversionResult(null)
      setShowResults(false)
    },
  }

  /**
   * Polls for the result of an asynchronous conversion.
   * @async
   * @param {string} asyncID - The ID of the async operation to poll
   */
  const pollForAsyncResult = async (asyncID: string): Promise<void> => {
    try {
      const pollResult = await pollingAsync(asyncID)
      if (pollResult.status === 200) {
        const asyncResult = await getAsyncResult(asyncID)
        React.startTransition(() => {
          setConversionResult(asyncResult.data as AIConversionResponse)
          setShowResults(true)
          setIsConverting(false)
          void storeSourceCodes(asyncResult.data as AIConversionResponse)

          if ((asyncResult.data as AIConversionResponse).success) {
            notify.toast.success("Questionnaire converted successfully!")
          } else {
            notify.toast.warning(
              "Conversion completed with errors. Please review the results."
            )
          }
        })
      } else {
        setTimeout(() => pollForAsyncResult(asyncID), 5000)
      }
    } catch (error) {
      console.error("Polling error:", error)
      setIsConverting(false)
      notify.toast.error(
        `Conversion failed: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      )
    }
  }

  /**
   * Handles the conversion process
   */
  const handleConvert = async () => {
    if (fileList.length === 0) {
      notify.toast.error("Please select a file first!")
      return
    }

    const file = fileList[0]

    // Handle both wrapped and direct file objects
    const actualFile = file.originFileObj || file

    // Verify we have a valid File object
    if (!(actualFile instanceof File)) {
      notify.toast.error("Invalid file selected!")
      return
    }

    try {
      setShowResults(false)
      setConversionResult(null)
      setIsConverting(true)

      const response = await convertQuestionnaire(actualFile, projectId, true)

      if (response.status === 202) {
        pollForAsyncResult(response.data as string)
      } else {
        setConversionResult(response.data as AIConversionResponse)
        setShowResults(true)
        setIsConverting(false)
        void storeSourceCodes(response.data as AIConversionResponse)

        if ((response.data as AIConversionResponse).success) {
          notify.toast.success("Questionnaire converted successfully!")
        } else {
          notify.toast.warning(
            "Conversion completed with errors. Please review the results."
          )
        }
      }
    } catch (error: unknown) {
      console.error("Conversion error:", error)
      setIsConverting(false)
      notify.toast.error(
        `Conversion failed: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      )
    }
  }

  /**
   * Inserts the converted content into the editor
   */
  const handleInsertContent = () => {
    if (!conversionResult?.data?.dataqContent || !editor) {
      notify.toast.error("No content to insert or editor not available!")
      return
    }

    try {
      // Build the full replacement HTML from the converted lines.
      // Empty lines become <p><br></p> to preserve spacing.
      const lines = conversionResult.data.dataqContent
        .split(/\r?\n/) // Handle both \n and \r\n line endings
        .map((line) => line.trim())

      const content = lines.length === 0
        ? "<p><br></p>"
        : lines
            .map((line) => line === "" ? "<p><br></p>" : "<p>" + escapeHTML(line) + "</p>")
            .join("")

      // The AI call is long-running — the editor may have been torn down
      // in the meantime (destroyed editors have no schema).
      if (editor.isDestroyed) {
        notify.toast.error("Editor is no longer available — please try again.")
        return
      }

      // Replace editor content via a single ProseMirror transaction so y-prosemirror
      // can diff against the existing Yjs state and produce minimal ops.
      // Using clearContent().insertContent() would emit two separate transactions,
      // piling delete tombstones + new inserts in the Yjs doc and causing duplication.
      const element = document.createElement("div")
      element.innerHTML = content
      const newDoc = ProseMirrorDOMParser.fromSchema(editor.schema).parse(element)
      const { state, view } = editor
      view.dispatch(
        state.tr.replaceWith(0, state.doc.content.size, newDoc.content)
      )
      editor.commands.focus()

      notify.toast.success("Content inserted into editor successfully!")
      onInserted?.({
        sourceCodesCaptured:
          conversionResult.data.sourceCodes?.entries?.length ?? 0,
      })
      onClose()
    } catch (error) {
      console.error("Error inserting content:", error)
      notify.toast.error("Failed to insert content into editor!")
    }
  }

  /**
   * Resets the modal state
   */
  const handleReset = () => {
    setFileList([])
    setConversionResult(null)
    setShowResults(false)
  }

  /**
   * Handles modal close
   */
  const handleClose = () => {
    handleReset()
    onClose()
  }

  /**
   * Renders conversion results
   */
  const renderResults = () => {
    if (!conversionResult || !showResults) return null

    const { data } = conversionResult
    const hasErrors = data.validation.errors.length > 0
    const hasWarnings = data.validation.warnings.length > 0

    return (
      <div style={{ marginTop: 16 }}>
        <Divider>Conversion Results</Divider>

        {/* Status Summary */}
        <Alert
          type={conversionResult.success ? "success" : "warning"}
          message={
            <Space>
              {conversionResult.success ? (
                <CheckCircleOutlined />
              ) : (
                <ExclamationCircleOutlined />
              )}
              {conversionResult.message}
            </Space>
          }
          description={
            <div>
              <Space>
                <Text>Processing time:</Text>
                <Text strong style={{ color: "#36c3ed" }}>
                  {formatProcessingTime(data.processingTimeMs)}
                </Text>
              </Space>
              {data.maxIterationsReached && (
                <>
                  <br />
                  <Text type="warning">Maximum iterations reached</Text>
                </>
              )}
              {(data.sourceCodes?.entries?.length ?? 0) > 0 && (
                <>
                  <br />
                  <Text type="secondary">
                    Source codes captured: {data.sourceCodes?.entries.length}{" "}
                    questions
                  </Text>
                </>
              )}
            </div>
          }
          style={{ marginBottom: 16 }}
        />

        {/* Validation Results */}
        {(hasErrors || hasWarnings) && (
          <Collapse style={{ marginBottom: 16 }}>
            {hasErrors && (
              <Panel
                header={
                  <Space>
                    <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />
                    <Text strong>Errors ({data.validation.errors.length})</Text>
                  </Space>
                }
                key="errors"
              >
                <List
                  size="small"
                  dataSource={data.validation.errors}
                  renderItem={(error, index) => (
                    <List.Item>
                      <Space direction="vertical" style={{ width: "100%" }}>
                        <Text strong>Error {index + 1}:</Text>
                        <Text>{error.message}</Text>
                        <Text type="secondary">
                          Line {error["line nbr"]} | Code: {error.errcode}
                        </Text>
                      </Space>
                    </List.Item>
                  )}
                />
              </Panel>
            )}

            {hasWarnings && (
              <Panel
                header={
                  <Space>
                    <WarningOutlined style={{ color: "#faad14" }} />
                    <Text strong>
                      Warnings ({data.validation.warnings.length})
                    </Text>
                  </Space>
                }
                key="warnings"
              >
                <List
                  size="small"
                  dataSource={data.validation.warnings}
                  renderItem={(warning, index) => (
                    <List.Item>
                      <Space direction="vertical" style={{ width: "100%" }}>
                        <Text strong>Warning {index + 1}:</Text>
                        <Text>{warning.message}</Text>
                        <Text type="secondary">
                          Line {warning["line nbr"]} | Code: {warning.errcode}
                        </Text>
                      </Space>
                    </List.Item>
                  )}
                />
              </Panel>
            )}
          </Collapse>
        )}

        {/* Content Preview */}
        <Collapse>
          <Panel
            header={
              <Space>
                <FileTextOutlined />
                <Text strong>Generated DATA(Q) Content</Text>
                <Tag color="#36c3ed">
                  {data.dataqContent?.length || 0} characters
                </Tag>
              </Space>
            }
            key="content"
          >
            <div
              style={{
                backgroundColor: "#f5f5f5",
                padding: 12,
                borderRadius: 4,
                maxHeight: 300,
                overflow: "auto",
                fontFamily: "monospace",
                whiteSpace: "pre-wrap",
                fontSize: "12px",
              }}
            >
              {data.dataqContent || "No content available"}
            </div>
          </Panel>
        </Collapse>

        {/* Action Buttons */}
        <div style={{ marginTop: 16, textAlign: "right" }}>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              Convert Another File
            </Button>
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={handleInsertContent}
              style={{ backgroundColor: "#36c3ed", borderColor: "#36c3ed" }}
              // disabled={!data.success}
            >
              Insert into Editor
            </Button>
          </Space>
        </div>
      </div>
    )
  }

  return (
    <Modal
      title="Convert Questionnaire using AI"
      open={isVisible}
      onCancel={handleClose}
      width={800}
      footer={null}
      destroyOnHidden={true}
    >
      <div>
        <Paragraph>
          Upload a DOCX questionnaire file to convert it to DATA(Q) format using
          AI. The conversion process analyzes your document structure and
          generates properly formatted DATA(Q) syntax.
        </Paragraph>

        {/* Disclaimers */}
        <div className="p-4 border border-[#36c3ed] rounded-lg ">
          <div className="mb-2">
            <span className="text-base font-medium text-[#36c3ed]">
              Important Information
            </span>
          </div>
          <div className="text-xs text-gray-800">
            AI conversion results may vary in accuracy and completeness. Always
            manually review and verify the generated content. Complex document
            structures may require additional editing, and the AI may not
            capture all nuances from the original document.
          </div>
        </div>

        {/* File Upload - Hidden during conversion and when results are shown */}
        {!isConverting && !showResults && (
          <Dragger {...uploadProps} style={{ marginBottom: 16, marginTop: 16 }}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag a DOCX file to this area to upload
            </p>
            <p className="ant-upload-hint">
              Support for single .docx file upload. Maximum file size: 10MB
            </p>
          </Dragger>
        )}

        {/* Conversion Progress */}
        {(isConvertingQuestionnaire || isConverting) && (
          <div className="text-center mb-4 py-10 w-full shadow-md mt-2 rounded-2xl">
            <div className=" w-1/2 mx-auto">
              <DotLottieReact
                src="https://lottie.host/f007d418-98df-4b63-8d88-9c4a1b1f966c/ioEc80sIK0.lottie"
                loop
                autoplay
              />
            </div>

            <Text style={{ fontSize: 22, fontWeight: 500, color: "#36c3ed" }}>
              Converting Document
            </Text>

            <div style={{ marginTop: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                This may take a few moments depending on document complexity
              </Text>
            </div>
          </div>
        )}

        {/* Convert Button */}
        {fileList.length > 0 && !showResults && !isConverting && (
          <div style={{ textAlign: "center", marginBottom: 16 }}>
            <Button
              type="primary"
              size="large"
              loading={isConvertingQuestionnaire || isConverting}
              onClick={handleConvert}
              disabled={fileList.length === 0}
              style={{ backgroundColor: "#36c3ed", borderColor: "#36c3ed" }}
            >
              Convert to DATA(Q)
            </Button>
          </div>
        )}

        {/* Results */}
        {renderResults()}
      </div>
    </Modal>
  )
}

export default AIConversionModal
